/**
 * Shared dashboard wire types.
 *
 * Both the platform overview (`/api/v1/platform/overview`) and the
 * tenant overview (`/api/v1/tenants/{id}/overview` and
 * `/api/tenant/v1/overview`) surface a common envelope: alert list
 * + 14-day sign-in trend. Extracting the shapes here lets both
 * features' Zod schemas import identical types and lets the shared
 * panel components render either overview without feature-specific
 * plumbing.
 *
 * If a new alert kind is added on the backend it MUST also land in
 * `dashboardAlertKindSchema` here in the same commit — an unknown
 * kind makes Zod reject the whole overview response.
 */

import { z } from "zod";

export const dashboardAlertKindSchema = z.enum([
  "tenant_no_passkeys",
  "webhook_failing",
  "policy_block_spike",
  "admin_invite_stale",
  "api_key_expiring",
]);
export type DashboardAlertKind = z.infer<typeof dashboardAlertKindSchema>;

export const dashboardAlertSeveritySchema = z.enum(["info", "warning", "danger"]);
export type DashboardAlertSeverity = z.infer<typeof dashboardAlertSeveritySchema>;

export const dashboardAlertSchema = z.object({
  id: z.string(),
  kind: dashboardAlertKindSchema,
  severity: dashboardAlertSeveritySchema,
  title: z.string(),
  description: z.string(),
  /** Absolute path the alert's "Investigate" link should go to. */
  href: z.string().nullable(),
  tenant_slug: z.string().nullable(),
  created_at: z.string(),
});
export type DashboardAlert = z.infer<typeof dashboardAlertSchema>;

export const signInTrendPointSchema = z.object({
  date: z.string(),
  success: z.number().int().nonnegative(),
  failure: z.number().int().nonnegative(),
});
export type SignInTrendPoint = z.infer<typeof signInTrendPointSchema>;
